import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";
import type { WaitlistStatus, WaitlistAdminView, WaitlistListResponse } from "@/lib/api/generated";
import { Spinner } from "@/components/ui/spinner";
import { getWaitlistEntriesServer, approveWaitlistServer } from "@/server/admin";
import { logError } from "@/lib/structured-log";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface SearchParams {
  page?: number;
  status?: WaitlistStatus | "all";
  entryId?: string;
}

const PAGE_SIZE = 20;

interface LoaderData {
  entries: WaitlistAdminView[];
  total: number;
}

export const Route = createFileRoute("/admin/waitlist")({
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    status: (search.status as WaitlistStatus | "all") || "all",
    entryId: typeof search.entryId === "string" ? search.entryId : undefined,
  }),
  loaderDeps: ({ search }) => ({
    page: search.page || 1,
    status: search.status || "all",
  }),
  loader: async ({ deps }): Promise<LoaderData> => {
    const skip = ((deps.page || 1) - 1) * PAGE_SIZE;
    const response = (await getWaitlistEntriesServer({
      data: {
        skip,
        limit: PAGE_SIZE,
        status: deps.status === "all" ? undefined : deps.status,
      },
    })) as WaitlistListResponse;
    return {
      entries: response.items,
      total: response.total,
    };
  },
  component: WaitlistAdminPage,
});

function WaitlistAdminPage() {
  const { t } = useTranslation("admin");
  const { entries, total } = Route.useLoaderData();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();

  const currentPage = search.page || 1;
  const currentStatus = search.status || "all";
  const currentEntryId = search.entryId;

  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());

  // Use total count from server to calculate total pages
  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(total / PAGE_SIZE));
  }, [total]);

  // Auto-select first entry if none selected
  useEffect(() => {
    if (currentEntryId || entries.length === 0) return;

    navigate({
      search: {
        page: currentPage,
        status: currentStatus,
        entryId: entries[0]?.id,
      },
      replace: true,
    });
  }, [currentPage, currentStatus, currentEntryId, entries, navigate]);

  const selectedEntry = useMemo(() => {
    if (!currentEntryId) return null;
    return entries.find((e) => e.id === currentEntryId) || null;
  }, [currentEntryId, entries]);

  const handlePageChange = (newPage: number) => {
    const safePage = Math.min(Math.max(1, newPage), totalPages);
    navigate({
      search: {
        page: safePage,
        status: currentStatus,
        entryId: undefined,
      },
    });
  };

  const handleStatusChange = (newStatus: string) => {
    navigate({
      search: {
        page: 1,
        status: newStatus as WaitlistStatus | "all",
        entryId: undefined,
      },
    });
  };

  const handleSelectEntry = (entryId: string) => {
    navigate({
      search: {
        page: currentPage,
        status: currentStatus,
        entryId,
      },
    });
  };

  const handleApprove = async (waitlistId: string) => {
    if (loadingIds.has(waitlistId)) return;

    setLoadingIds((prev) => new Set(prev).add(waitlistId));

    try {
      await approveWaitlistServer({ data: { waitlistId } });
      toast.success(t("waitlist.actions.approveSuccess"));

      // Refresh the page data
      navigate({
        search: { page: currentPage, status: currentStatus, entryId: currentEntryId },
      });
    } catch (error) {
      logError("admin.approve_waitlist.client_error", {
        waitlist_id: waitlistId,
        error: error instanceof Error ? error.message : "unknown-error",
      });
      toast.error(t("waitlist.actions.approveError"));
    } finally {
      setLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(waitlistId);
        return next;
      });
    }
  };

  return (
    <div className="grid h-full min-h-0 grid-cols-1 lg:grid-cols-[420px_minmax(0,1fr)]">
      {/* Sidebar - Entry List */}
      <aside className="flex h-full min-h-0 flex-col overflow-hidden border-b bg-muted/20 lg:border-b-0 lg:border-r">
        <div className="flex shrink-0 items-center justify-between border-b px-5 py-4">
          <div className="space-y-0.5">
            <div className="text-sm font-medium">{t("waitlist.sidebar.title")}</div>
            <div className="text-xs text-muted-foreground">
              {t("waitlist.sidebar.total", { total })} •{" "}
              {t("waitlist.sidebar.pageInfo", { current: currentPage, total: totalPages })}
            </div>
          </div>
          <Badge variant="secondary" className="shrink-0">
            {total}
          </Badge>
        </div>

        <div className="shrink-0 border-b px-5 py-3">
          <Select value={currentStatus} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t("waitlist.sidebar.filter")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("waitlist.filters.all")}</SelectItem>
              <SelectItem value="pending">{t("waitlist.filters.pending")}</SelectItem>
              <SelectItem value="approved">{t("waitlist.filters.approved")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          {entries.length === 0 ? (
            <div className="flex h-full items-center justify-center px-4 py-12 text-sm text-muted-foreground">
              {t("waitlist.sidebar.empty")}
            </div>
          ) : (
            <ul className="divide-y">
              {entries.map((entry) => (
                <WaitlistEntryRow
                  key={entry.id}
                  entry={entry}
                  isSelected={currentEntryId === entry.id}
                  onSelect={handleSelectEntry}
                  t={t}
                />
              ))}
            </ul>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-between border-t px-5 py-4">
          <div className="text-xs text-muted-foreground">
            {t("waitlist.sidebar.showing", { count: entries.length })}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
            >
              {t("waitlist.sidebar.previous")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
            >
              {t("waitlist.sidebar.next")}
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content - Entry Detail */}
      <main className="relative min-h-0 min-w-0 overflow-hidden bg-background">
        <div className="h-full overflow-auto px-4 py-4 lg:px-6 lg:py-5">
          <WaitlistDetailCard
            entry={selectedEntry}
            loadingIds={loadingIds}
            onApprove={handleApprove}
            t={t}
          />
        </div>
      </main>
    </div>
  );
}

function WaitlistEntryRow({
  entry,
  isSelected,
  onSelect,
  t,
}: {
  entry: WaitlistAdminView;
  isSelected: boolean;
  onSelect: (entryId: string) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const payload = entry.form_payload as Record<string, string | undefined>;
  const userName =
    payload.name || `${entry.user.first_name ?? ""} ${entry.user.last_name ?? ""}`.trim() || "-";

  const handleSelect = () => {
    onSelect(entry.id);
  };

  return (
    <li>
      <button
        type="button"
        onClick={handleSelect}
        aria-pressed={isSelected}
        className={[
          "relative w-full text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          isSelected ? "bg-muted/60" : "hover:bg-muted/40",
        ].join(" ")}
      >
        <span
          aria-hidden="true"
          className={[
            "absolute inset-y-2 left-0 w-0.5 rounded-full bg-primary transition-opacity",
            isSelected ? "opacity-100" : "opacity-0",
          ].join(" ")}
        />
        <div className="flex items-start justify-between gap-3 px-5 py-4">
          <div className="min-w-0 space-y-1.5">
            <div className="flex items-center gap-2">
              <div className="truncate text-sm font-medium">{userName}</div>
              <WaitlistStatusBadge status={entry.status} t={t} />
            </div>
            <div className="truncate text-xs text-muted-foreground">
              <span className="text-muted-foreground/80">{t("waitlist.entry.email")}:</span>{" "}
              {entry.user.email || "-"}
            </div>
            <div className="truncate text-xs text-muted-foreground">
              <span className="text-muted-foreground/80">{t("waitlist.entry.company")}:</span>{" "}
              {payload.company_name || "-"}
            </div>
            <div className="text-[11px] text-muted-foreground/90">
              {t("waitlist.entry.submitted")}: {formatDate(entry.created_at)}
            </div>
          </div>

          <div className="shrink-0">
            <span
              className={[
                "inline-flex h-8 items-center rounded-md border px-3 text-xs font-medium",
                isSelected
                  ? "border-transparent bg-secondary text-secondary-foreground"
                  : "border-border bg-background text-foreground",
              ].join(" ")}
            >
              {isSelected ? t("waitlist.sidebar.selected") : t("waitlist.sidebar.view")}
            </span>
          </div>
        </div>
      </button>
    </li>
  );
}

function WaitlistDetailCard({
  entry,
  loadingIds,
  onApprove,
  t,
}: {
  entry: WaitlistAdminView | null;
  loadingIds: Set<string>;
  onApprove: (entryId: string) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  if (!entry) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center rounded-xl border border-dashed text-sm text-muted-foreground">
        {t("waitlist.detail.selectPrompt")}
      </div>
    );
  }

  const payload = entry.form_payload as Record<string, string | undefined>;
  const userName =
    payload.name || `${entry.user.first_name ?? ""} ${entry.user.last_name ?? ""}`.trim() || "-";
  const isApproving = loadingIds.has(entry.id);

  const formatFormValue = (value: string | undefined) => {
    if (!value || value.trim() === "") return "-";
    return value;
  };

  const formatEnumValue = (value: string | undefined, type: "role" | "source") => {
    if (!value) return "-";
    const translationKey =
      type === "role"
        ? `pages:onboarding.waitlist.options.roles.${value}`
        : `pages:onboarding.waitlist.options.sources.${value}`;
    const translated = t(translationKey);
    return translated !== translationKey ? translated : value;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <header className="space-y-3 border-b pb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 space-y-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-2xl font-semibold tracking-tight">{userName}</h2>
              <WaitlistStatusBadge status={entry.status} t={t} />
            </div>
            <div className="text-sm text-muted-foreground">{entry.user.email ?? "-"}</div>
          </div>

          {entry.status === "pending" && (
            <Button
              size="sm"
              onClick={() => onApprove(entry.id)}
              disabled={isApproving}
              className="shrink-0"
            >
              {isApproving ? (
                <span className="inline-flex items-center gap-2">
                  <Spinner className="h-4 w-4" />
                  {t("waitlist.actions.approving")}
                </span>
              ) : (
                <span>{t("waitlist.actions.approve")}</span>
              )}
            </Button>
          )}
        </div>

        {/* Meta Info */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px] text-muted-foreground">
          <MetaChip
            label={t("waitlist.detail.id")}
            value={shortenId(entry.id)}
            copyValue={entry.id}
          />
          <MetaChip
            label={t("waitlist.detail.userId")}
            value={shortenId(entry.user.id)}
            copyValue={entry.user.id}
          />
          <MetaChip label={t("waitlist.detail.submitted")} value={formatDate(entry.created_at)} />
          {entry.reviewed_at && (
            <MetaChip label={t("waitlist.detail.reviewed")} value={formatDate(entry.reviewed_at)} />
          )}
        </div>
      </header>

      {/* Content Sections */}
      <div className="divide-y divide-border/80">
        {/* User Information Section */}
        <section className="py-3">
          <DetailBlock title={t("waitlist.detail.userInfo")}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <CompactField
                label={t("waitlist.detail.fields.name")}
                value={formatFormValue(userName)}
              />
              <CompactField
                label={t("waitlist.detail.fields.email")}
                value={formatFormValue(entry.user.email ?? undefined)}
              />
              <CompactField
                label={t("waitlist.detail.fields.phone")}
                value={formatFormValue(payload.phone_number)}
              />
              <CompactField
                label={t("waitlist.detail.fields.jobTitle")}
                value={formatEnumValue(payload.job_title, "role")}
              />
              <CompactField
                label={t("waitlist.detail.fields.company")}
                value={formatFormValue(payload.company_name)}
              />
              <CompactField
                label={t("waitlist.detail.fields.website")}
                value={
                  payload.website ? (
                    <a
                      href={payload.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-foreground underline-offset-4 hover:underline"
                    >
                      {payload.website}
                      <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                    </a>
                  ) : (
                    "-"
                  )
                }
              />
            </div>
          </DetailBlock>
        </section>

        {/* Form Data Section */}
        <section className="py-3">
          <DetailBlock title={t("waitlist.detail.formData")}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <CompactField
                label={t("waitlist.detail.fields.source")}
                value={formatEnumValue(payload.source, "source")}
              />
              <CompactField
                label={t("waitlist.detail.fields.referral")}
                value={formatFormValue(payload.referral)}
              />
            </div>
            {payload.comments && (
              <div className="mt-4">
                <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  {t("waitlist.detail.fields.comments")}
                </div>
                <div className="mt-1.5 rounded-xl border border-border/80 bg-muted/20 px-4 py-3 text-sm leading-6 text-foreground/90">
                  {payload.comments}
                </div>
              </div>
            )}
          </DetailBlock>
        </section>

        {/* System Information Section */}
        <section className="py-3">
          <DetailBlock title={t("waitlist.detail.systemInfo")}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <CompactField
                label={t("waitlist.detail.fields.clerkId")}
                value={formatFormValue(entry.user.clerk_user_id)}
              />
              <CompactField label={t("waitlist.detail.fields.status")} value={entry.status} />
              {entry.review_comment && (
                <CompactField
                  label={t("waitlist.detail.fields.reviewComment")}
                  value={entry.review_comment}
                />
              )}
            </div>
          </DetailBlock>
        </section>
      </div>
    </div>
  );
}

// UI Components

function DetailBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      {children}
    </div>
  );
}

function CompactField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0 space-y-1">
      <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground/90">
        {label}
      </div>
      <div className="min-w-0 truncate whitespace-nowrap text-sm leading-5 text-foreground/90">
        {value}
      </div>
    </div>
  );
}

function WaitlistStatusBadge({
  status,
  t,
}: {
  status: WaitlistStatus;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  if (status === "pending") {
    return (
      <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">
        {t("waitlist.status.pending")}
      </Badge>
    );
  }

  return (
    <Badge variant="outline" className="border-green-200 bg-green-50 text-green-700">
      {t("waitlist.status.approved")}
    </Badge>
  );
}

function MetaChip({
  label,
  value,
  copyValue,
}: {
  label: string;
  value: string;
  copyValue?: string;
}) {
  const copyToClipboard = async (text: string) => {
    if (typeof navigator === "undefined" || !navigator.clipboard?.writeText) {
      toast.error("Clipboard unavailable");
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed");
    }
  };

  const rendered = value.trim() === "" ? "-" : value;

  return (
    <div className="inline-flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
      <span className="shrink-0 uppercase tracking-wide text-muted-foreground/80">{label}:</span>
      <span className="min-w-0 truncate whitespace-nowrap font-mono text-foreground/90">
        {rendered}
      </span>
      {copyValue ? (
        <button
          type="button"
          onClick={() => void copyToClipboard(copyValue)}
          className="shrink-0 text-muted-foreground/70 transition-colors hover:text-foreground"
          aria-label={`Copy ${label}`}
        >
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        </button>
      ) : null}
    </div>
  );
}

// Utilities

function formatDate(value: string | null | undefined): string {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  const formatter = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZoneName: "short",
  });

  const parts = formatter.formatToParts(date);
  const pick = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? "";

  const year = pick("year");
  const month = pick("month");
  const day = pick("day");
  const hour = pick("hour");
  const minute = pick("minute");
  const second = pick("second");
  const timeZoneName = pick("timeZoneName");

  const core = `${year}-${month}-${day} ${hour}:${minute}:${second}`;
  return timeZoneName ? `${core} ${timeZoneName}` : core;
}

function shortenId(value: string | null | undefined, prefix = 8, suffix = 4): string {
  if (!value) return "-";
  if (value.length <= prefix + suffix + 1) return value;
  return `${value.slice(0, prefix)}…${value.slice(-suffix)}`;
}
